import type { FieldProps, TextInputProps } from '@hover/blueprint';
import { Field } from '@hover/blueprint';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { RevealablePasswordInput } from './RevealablePasswordInput';

interface PasswordFieldProps
  extends Omit<FieldProps, 'children'>,
    Pick<TextInputProps, 'placeholder'> {}

const PasswordField = ({ name, placeholder, ...props }: PasswordFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { errors, formState, register, unregister, setValue, trigger, watch } =
    useFormContext<Record<string, string>>();

  const error = errors[name]?.message;
  const value = watch(name);

  useEffect(() => {
    register(name);

    return () => unregister(name);
  }, [name, register, unregister]);

  const onChange: TextInputProps['onChange'] = event => {
    event.persist();
    setValue(name, event?.target.value);

    // NOTE: `reValidateMode` doesn't seem to work properly with this manual
    // registration setup. This emulates `reValidateMode="onChange"` behavior.
    if (formState.isSubmitted) void trigger(name);
  };

  return (
    <Field {...props} error={error} name={name}>
      <RevealablePasswordInput
        // FIXME: Field should be passing `isInvalid` but it doesn't know what
        // `PasswordInput` is. The solution here is pretty much just move
        // `PasswordInput` into @hover/blueprint which we should do regardless
        isInvalid={!!error}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
      />
    </Field>
  );
};

export { PasswordField };
